import { useEffect, useState } from 'react'
import { Link, useOutletContext } from 'react-router-dom'
import { apiGet } from '../shared/api'
import { useI18n } from '../i18n'

interface Site {
  id: string
  name: string
  type: string
}

interface SearchResult {
  id: string
  title: string
  poster: string
  year?: string
  type?: string
}

export default function Home() {
  const { me } = useOutletContext<{ me: { id: string; email: string; displayName: string; role?: 'user' | 'admin' } | null }>()
  const [sites, setSites] = useState<Site[]>([])
  const [siteId, setSiteId] = useState('demo')
  const [q, setQ] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [autoItems, setAutoItems] = useState<SearchResult[]>([])
  const [autoLoading, setAutoLoading] = useState(false)
  const { t } = useI18n()

  useEffect(() => {
    if (!me) return
    apiGet('/api/sites')
      .then((res) => {
        setSites(res.sites || [])
        if (res.sites?.length > 0) {
          setSiteId(res.sites[0].id)
        }
      })
      .catch(() => setSites([]))
  }, [me])

  // 自动根据站点加载资源列表（参考 LibreTV 的站点API约定）
  useEffect(() => {
    if (!me) return
    if (!siteId) return
    if (searched) return // 搜索态下不干扰用户

    const load = async () => {
      setAutoLoading(true)
      setAutoItems([])
      // 依次尝试常见的资源端点，然后回退到关键词搜索
      const tryEndpoints = [
        `/api/sites/${siteId}/home`,
        `/api/sites/${siteId}/recommend`,
        `/api/sites/${siteId}/latest`,
      ]
      let items: SearchResult[] = []
      for (const ep of tryEndpoints) {
        try {
          const res = await apiGet<{ items?: SearchResult[] }>(ep)
          if (Array.isArray(res.items) && res.items.length) {
            items = res.items
            break
          }
        } catch {
          // 忽略单个端点失败，继续尝试
        }
      }

      // 回退：用常见关键词进行一次站点内搜索
      if (items.length === 0) {
        const fallbacks = ['热门', '推荐', '最新']
        for (const keyword of fallbacks) {
          try {
            const res = await apiGet<{ items?: SearchResult[] }>(
              `/api/sites/${siteId}/search?q=${encodeURIComponent(keyword)}`
            )
            if (Array.isArray(res.items) && res.items.length) {
              items = res.items
              break
            }
          } catch {
            // 继续尝试下一个关键词
          }
        }
      }

      setAutoItems(items)
      setAutoLoading(false)
    }

    void load()
  }, [me, siteId, searched])

  const doSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!q.trim()) return
    
    setLoading(true)
    setSearched(true)
    try {
      const res = await apiGet(`/api/sites/${siteId}/search?q=${encodeURIComponent(q.trim())}`)
      setResults(res.items || [])
    } catch (error) {
      console.error('Search failed:', error)
      setResults([])
    } finally {
      setLoading(false)
    }
  }

  const clearSearch = () => {
    setQ('')
    setResults([])
    setSearched(false)
  }

  if (!me) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div className="flex justify-center">
            <img
              src="/images/moetv.png"
              alt="MoeTV"
              className="w-full max-w-md rounded-lg shadow-sm"
            />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-3">{t('app.name')}</h1>
            <p className="text-gray-600 mb-6">{t('app.slogan')}</p>

            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start"><span className="mt-1 mr-2">✅</span><span>{t('home.features.aggregate')}</span></li>
              <li className="flex items-start"><span className="mt-1 mr-2">✅</span><span>{t('home.features.hls')}</span></li>
              <li className="flex items-start"><span className="mt-1 mr-2">✅</span><span>{t('home.features.simple')}</span></li>
              <li className="flex items-start"><span className="mt-1 mr-2">✅</span><span>{t('home.features.extensible')}</span></li>
            </ul>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/login"
                className="px-5 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
              >
                {t('home.loginNow')}
              </Link>
              <Link
                to="/register"
                className="px-5 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                {t('home.register')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('app.name')}</h1>
        <p className="text-gray-600">{t('home.intro')}</p>
      </div>

      <form onSubmit={doSearch} className="flex flex-col md:flex-row gap-3 max-w-4xl mx-auto">
        <select 
          value={siteId} 
          onChange={(e) => setSiteId(e.target.value)} 
          className="border rounded-lg px-4 py-3 bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {sites.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
        <input 
          value={q} 
          onChange={(e) => setQ(e.target.value)} 
          className="flex-1 border rounded-lg px-4 py-3 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
          placeholder={t('home.placeholder')} 
        />
        <button 
          type="submit"
          disabled={loading || !q.trim()}
          className="px-6 py-3 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? t('common.searching') : t('common.search')}
        </button>
        {searched && (
          <button 
            type="button"
            onClick={clearSearch}
            className="px-4 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {t('common.clear')}
          </button>
        )}
      </form>

      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <div className="mt-2 text-gray-600">{t('common.searching')}</div>
        </div>
      )}

      {searched && !loading && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {t('home.results')} ({results.length})
            </h2>
            {results.length > 0 && (
              <span className="text-sm text-gray-500">
                {t('home.foundInSite', { count: results.length, site: sites.find(s => s.id === siteId)?.name || '' })}
              </span>
            )}
          </div>
          
          {results.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🔍</div>
              <div className="text-gray-500">{t('common.notFound')}</div>
              <div className="text-sm text-gray-400 mt-2">{t('common.tryOtherKeywords')}</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {results.map((item) => (
                <Link 
                  key={item.id} 
                  to={`/detail/${siteId}/${encodeURIComponent(item.id)}`} 
                  className="group bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden border border-gray-100 hover:border-blue-200"
                >
                  <div className="relative">
                    <img 
                      src={item.poster} 
                      alt={item.title} 
                      className="w-full aspect-[2/3] object-cover group-hover:scale-105 transition-transform duration-200" 
                    />
                    {item.type && (
                      <div className="absolute top-2 right-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                        {item.type}
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="font-medium text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors">
                      {item.title}
                    </div>
                    {item.year && (
                      <div className="text-sm text-gray-500 mt-1">{item.year}</div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {!searched && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {t('common.siteResources')}
            </h2>
            <span className="text-sm text-gray-500">
              {sites.find(s => s.id === siteId)?.name}
            </span>
          </div>

          {autoLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <div className="mt-2 text-gray-600">{t('common.loadSiteResources')}</div>
            </div>
          ) : autoItems.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🎬</div>
              <div className="text-gray-500">{t('common.siteNoHomeFeed')}</div>
              <div className="text-sm text-gray-400 mt-2">{t('common.trySearchDirectly')}</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {autoItems.map((item) => (
                <Link 
                  key={item.id} 
                  to={`/detail/${siteId}/${encodeURIComponent(item.id)}`} 
                  className="group bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden border border-gray-100 hover:border-blue-200"
                >
                  <div className="relative">
                    <img 
                      src={item.poster} 
                      alt={item.title} 
                      className="w-full aspect-[2/3] object-cover group-hover:scale-105 transition-transform duration-200" 
                    />
                    {item.type && (
                      <div className="absolute top-2 right-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                        {item.type}
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="font-medium text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors">
                      {item.title}
                    </div>
                    {item.year && (
                      <div className="text-sm text-gray-500 mt-1">{item.year}</div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
